/**
 * React Query hooks + helpers for Stores > Employee purchases.
 *
 *   useEmployeePurchases(filters, sort, enabled)  GET /v1/stores/employee-purchases
 *   downloadEmployeePurchasesXlsx(filters, sort)  GET /v1/stores/employee-purchases/export
 *
 * One row per line of every CounterPoint ticket rung with the EMPLOYEE
 * discount (capability stores.employeepurchases). The api owns sort, filters,
 * paging and totals, so later pages and the Excel export always follow the
 * order on screen. Same 404/503 "not deployed yet" convention as useStores.ts.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { apiGet } from './api';
import { STORES_API_BASE } from './useStores';

export const EMPLOYEE_PURCHASES_API_BASE = `${STORES_API_BASE}/employee-purchases`;
export const EMPLOYEE_PURCHASES_PAGE_SIZE = 200;
export const EMPLOYEE_PURCHASES_DEFAULT_DAYS = 30;
export const EMPLOYEE_PURCHASES_MAX_DAYS = 366;

export type EmployeePurchaseLine = {
  doc_id: string;
  line_no: number;
  bus_date: string;
  /** Store-local register time, "YYYY-MM-DD HH:MM:SS". */
  date_time: string | null;
  store_no: string;
  store_name: string | null;
  station: string | null;
  ticket_no: string;
  cashier_id: string | null;
  cashier_name: string | null;
  /** Null when the ticket was rung to the generic walk-in customer (most of them). */
  buyer_name: string | null;
  item_no: string | null;
  description: string | null;
  unit_price: number | null;
  regular_price: number | null;
  qty: number | null;
  subtotal: number | null;
  employee_discount: number;
  other_discount: number;
  other_discount_codes: string | null;
  discount: number;
  total: number | null;
  is_return: boolean;
  price_override: boolean;
  pay_types: string | null;
  paid_cash: boolean;
};

export type EmployeePurchaseTotals = {
  lines: number;
  tickets: number;
  qty: number;
  subtotal: number;
  employee_discount: number;
  other_discount: number;
  discount: number;
  total: number;
};

export type EmployeePurchasesResponse = {
  from: string;
  to: string;
  as_of: string | null;
  rows: EmployeePurchaseLine[];
  total_count: number;
  totals: EmployeePurchaseTotals;
  next_offset: number | null;
};

export type EmployeePurchasesSortColumn =
  | 'date_time'
  | 'store'
  | 'buyer'
  | 'cashier'
  | 'ticket'
  | 'item'
  | 'price'
  | 'qty'
  | 'subtotal'
  | 'discount'
  | 'total';

export type EmployeePurchasesSort = { column: EmployeePurchasesSortColumn; dir: 'asc' | 'desc' };

export const EMPLOYEE_PURCHASES_SORT_OPTIONS: ReadonlyArray<{ column: EmployeePurchasesSortColumn; label: string }> = [
  { column: 'date_time', label: 'Date / time' },
  { column: 'store', label: 'Store' },
  { column: 'buyer', label: 'Employee' },
  { column: 'cashier', label: 'Rung up by' },
  { column: 'ticket', label: 'Ticket' },
  { column: 'item', label: 'Line item' },
  { column: 'price', label: 'Sale price' },
  { column: 'qty', label: 'Qty' },
  { column: 'subtotal', label: 'Sub-total' },
  { column: 'discount', label: 'Discount' },
  { column: 'total', label: 'Total' },
];

const SORTABLE = new Set<string>(EMPLOYEE_PURCHASES_SORT_OPTIONS.map((o) => o.column));

/** `sort=<column>:<dir>` from the URL, or null for the default order (newest first). */
export function parseEmployeePurchasesSort(raw: string | null | undefined): EmployeePurchasesSort | null {
  if (!raw) return null;
  const [column, dir] = raw.split(':');
  if (!column || !SORTABLE.has(column)) return null;
  return { column: column as EmployeePurchasesSortColumn, dir: dir === 'desc' ? 'desc' : 'asc' };
}

export function formatEmployeePurchasesSort(sort: EmployeePurchasesSort): string {
  return `${sort.column}:${sort.dir}`;
}

export type EmployeePurchasesFilters = {
  from: string;
  to: string;
  store_no: string[];
  q: string;
  flagged: boolean;
};

/** The query string shared by the grid and the export, so the two never disagree. */
export function buildEmployeePurchasesQs(
  f: EmployeePurchasesFilters,
  sort: EmployeePurchasesSort | null,
  offset: number | null,
): string {
  const qs = new URLSearchParams({ from: f.from, to: f.to });
  if (f.store_no.length > 0) qs.set('store_no', f.store_no.join(','));
  if (f.q.trim()) qs.set('q', f.q.trim());
  if (f.flagged) qs.set('flagged', '1');
  if (sort) {
    qs.set('sort', sort.column);
    qs.set('dir', sort.dir);
  }
  if (offset !== null) {
    qs.set('limit', String(EMPLOYEE_PURCHASES_PAGE_SIZE));
    if (offset > 0) qs.set('offset', String(offset));
  }
  return qs.toString();
}

/** Days between two ISO dates, both ends included. */
export function rangeDays(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
}

/** A sentence describing what is wrong with the range, or null when it is usable. */
export function employeePurchasesRangeProblem(from: string, to: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return 'Pick a start and end date.';
  if (from > to) return 'The start date is after the end date.';
  if (rangeDays(from, to) > EMPLOYEE_PURCHASES_MAX_DAYS) return `Pick a range of ${EMPLOYEE_PURCHASES_MAX_DAYS} days or fewer.`;
  return null;
}

const EMPTY_TOTALS: EmployeePurchaseTotals = {
  lines: 0,
  tickets: 0,
  qty: 0,
  subtotal: 0,
  employee_discount: 0,
  other_discount: 0,
  discount: 0,
  total: 0,
};

export function useEmployeePurchases(
  filters: EmployeePurchasesFilters,
  sort: EmployeePurchasesSort | null,
  enabled: boolean,
) {
  const query = useInfiniteQuery<EmployeePurchasesResponse, Error>({
    queryKey: ['stores', 'employee-purchases', filters, sort],
    queryFn: ({ pageParam }) =>
      apiGet<EmployeePurchasesResponse>(
        `${EMPLOYEE_PURCHASES_API_BASE}?${buildEmployeePurchasesQs(filters, sort, (pageParam as number) ?? 0)}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.next_offset ?? undefined,
    staleTime: 5 * 60_000,
    retry: false,
    enabled,
  });
  const pages = query.data?.pages ?? [];
  const first = pages[0];
  return {
    /** Newest business date with data in the range; undefined until the first page lands. */
    asOf: first ? first.as_of : undefined,
    rows: pages.flatMap((p) => p.rows ?? []),
    totalCount: first?.total_count ?? null,
    totals: first?.totals ?? EMPTY_TOTALS,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => query.fetchNextPage(),
    error: query.error,
  };
}

/** Browser download of the Excel export, following the grid's filters and sort exactly. */
export async function downloadEmployeePurchasesXlsx(
  filters: EmployeePurchasesFilters,
  sort: EmployeePurchasesSort | null,
): Promise<void> {
  const res = await fetch(`${EMPLOYEE_PURCHASES_API_BASE}/export?${buildEmployeePurchasesQs(filters, sort, null)}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const m = (res.headers.get('content-disposition') ?? '').match(/filename="([^"]+)"/);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = m?.[1] ?? `employee-purchases-${filters.from}-to-${filters.to}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

/** "$1,234.56", "--" when null. Negative (return) amounts keep their minus sign. */
export function formatMoney(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '--';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** Quantity: whole numbers plain, weighed candy to up to three decimals. */
export function formatQty(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '--';
  return v.toLocaleString('en-US', { maximumFractionDigits: 3 });
}

/** "Oct 28, 2026 6:26 PM" from register wall time, never shifted to the viewer's zone. */
export function formatRegisterTime(dateTime: string | null, busDate: string): string {
  const m = dateTime ? /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.exec(dateTime) : null;
  if (!m) return busDate;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const h = Number(m[4]);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${months[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]} ${h12}:${m[5]} ${h < 12 ? 'AM' : 'PM'}`;
}
